import type { ObligationContract, StudentProfileContract } from "@chams-pay/contracts";

type AcademicYear = NonNullable<StudentProfileContract["academicYears"]>[number];

export interface StudentObligationVisibilityContext {
  readonly serviceSummaryFr: string;
  readonly calendar: Pick<StudentProfileContract["calendar"], "id">;
  readonly advanceYear?: Pick<
    NonNullable<StudentProfileContract["advanceYear"]>,
    "calendarSnapshotId"
  > | null;
  readonly academicYears?: readonly Pick<AcademicYear, "calendarSnapshotId" | "role">[];
}

function serviceSelected(
  student: StudentObligationVisibilityContext,
  family: ObligationContract["family"],
) {
  if (family === "transport") return /transport/i.test(student.serviceSummaryFr);
  if (family === "accompaniment") return /accompagnement/i.test(student.serviceSummaryFr);
  return true;
}

function followsCurrentSelection(
  student: StudentObligationVisibilityContext,
  obligation: ObligationContract,
) {
  if (obligation.calendarSnapshotId === student.calendar.id) return true;
  if (obligation.calendarSnapshotId === student.advanceYear?.calendarSnapshotId) return true;
  const role = student.academicYears?.find(
    (year) => year.calendarSnapshotId === obligation.calendarSnapshotId,
  )?.role;
  return role === "current" || role === "next";
}

/**
 * Hides fully neutralized, unselected service rows from current/next schedules.
 * Settled or unexpectedly payable rows stay visible so presentation never hides
 * financial history or an authoritative balance that still needs attention.
 */
export function visibleStudentObligations(
  student: StudentObligationVisibilityContext,
  obligations: readonly ObligationContract[],
): readonly ObligationContract[] {
  return obligations.filter(
    (obligation) =>
      !followsCurrentSelection(student, obligation) ||
      serviceSelected(student, obligation.family) ||
      BigInt(obligation.settledMad) > 0n ||
      BigInt(obligation.remainingMad) > 0n,
  );
}
